import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { withApplicationContext } from '../../../utils/ApplicationContext';
import { withLabels } from '../../../utils/LabelsContext';
import { is3pSeller } from '../../../utils/urlHelper';
import { ProductInfo } from '../../AdditionalInformation/ProductInfo/ProductInfo';
import SpecificationTable from '../../SpecificationTable/SpecificationTable';
import Accordion from '../Accordion/v2';
import ReturnPolicyContent from '../ReturnPolicyContent/ReturnPolicyContent';

const ACCORDION_OPEN_EVENT = 'openSpecificationsAccordion';

const ProductSpecificationsAccordion = ({
  product: {
    longDescription,
    attributes: { specifications = [] } = {},
    returnPolicy = {},
    sellerInfo: { sellerId = null } = {},
  },
  labels,
  labels: { SPECIFICATIONS_LABEL },
  appCtx: { regionCode, store },
  openSpecificationsItem,
}) => {
  const [openTrigger, setOpenTrigger] = useState(0);
  const is3P = sellerId ? is3pSeller(sellerId) : false;

  const hasDescriptionContent = () => {
    if (!longDescription) return false;

    // Check if description contains Flix Media script (dynamic content loader)
    const hasFlixContent = longDescription.includes('flix-inpage') || longDescription.includes('flixfacts.com');

    if (hasFlixContent) return true;

    // Check for regular text content
    const textContent = longDescription.replace(/<[^>]*>/g, '').trim();
    return textContent.length > 0;
  };

  const defaultItems = [
    {
      title: 'Descripción',
      content: <ProductInfo description={longDescription} />,
      isOpen: hasDescriptionContent(),
    },
    {
      title: SPECIFICATIONS_LABEL,
      content: <SpecificationTable specifications={specifications} />,
    },
  ];

  const returnPolicyItem = {
    title: returnPolicy.headerLabel,
    content: <ReturnPolicyContent regionCode={regionCode} labels={labels} store={store} />,
  };

  const orderedItems = is3P ? [returnPolicyItem, ...defaultItems] : [...defaultItems, returnPolicyItem];

  const specificationsItemIndex = is3P ? 2 : 1;

  useEffect(() => {
    const handleOpenSpecifications = () => {
      setOpenTrigger((prev) => prev + 1);
    };

    window.addEventListener(ACCORDION_OPEN_EVENT, handleOpenSpecifications);

    return () => {
      window.removeEventListener(ACCORDION_OPEN_EVENT, handleOpenSpecifications);
    };
  }, []);

  useEffect(() => {
    if (openTrigger > 0) {
      const timer = setTimeout(() => {
        setOpenTrigger(0);
      }, 200);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [openTrigger]);

  const shouldOpen = openSpecificationsItem || openTrigger > 0;

  return (
    <Accordion
      id="productSpecificationsAccordion"
      items={orderedItems}
      allowMultiple
      openItemIndex={shouldOpen ? specificationsItemIndex : null}
    />
  );
};

ProductSpecificationsAccordion.propTypes = {
  product: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  appCtx: PropTypes.object.isRequired,
  openSpecificationsItem: PropTypes.bool,
};

ProductSpecificationsAccordion.defaultProps = {
  openSpecificationsItem: false,
};

const ProductSpecificationsAccordionWithContext = withApplicationContext(withLabels(ProductSpecificationsAccordion));

export { ProductSpecificationsAccordion };
export default ProductSpecificationsAccordionWithContext;
